import {
  useDeleteLeaseCompImageMutation,
  useUpdateLeaseCompMediaStatusMutation,
} from "@/services/media";
import TransformedImage from "@components/TransformedImage";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import { EmeraldList, EmeraldListItem } from "@emerald-react/list";
import { EmeraldMenu } from "@emerald-react/menu";
import { EmeraldProgressSpinner } from "@emerald-react/progress-indicator";
import { useSortableItem } from "@hooks/useSortable";
import type { LeaseCompMediaFile } from "@survey-types/survey.leaseComp.types";
import classNames from "classnames";
import React, { startTransition, useOptimistic, useState } from "react";

interface LeaseCompPhotoThumbnailProps {
  eachFile: LeaseCompMediaFile;
  ownerId: string;
  surveyId: string;
}

const LeaseCompPhotoThumbnail = ({
  eachFile,
  ownerId,
  surveyId,
}: LeaseCompPhotoThumbnailProps) => {
  // TODO: Add lease comp media update endpoints for renaming and visibility toggling
  const [deleteLeaseCompImage] = useDeleteLeaseCompImageMutation();
  const [updateLeaseCompMediaStatus] = useUpdateLeaseCompMediaStatusMutation();

  const { attributes, listeners, setNodeRef, style, isDragging } =
    useSortableItem(eachFile?.mediaId || "");

  const fileExtension = eachFile?.displayName?.replace(/\.\w+$/, "") || "";
  const [fileName, setFileName] = useState(fileExtension);
  const [enableEdit, isEnableEdit] = useState(false);
  const [visibility, setVisibility] = useState(eachFile?.isHidden);
  const [manualLoading, setManualLoading] = useState(false);
  const [optimisticLoading, setOptimisticLoading] =
    useOptimistic(manualLoading);

  const isTempFile = eachFile?.mediaId.includes("temp-id-");
  const mediaType = "IMAGES";

  const setOptimisticLoadingState = (loading: boolean) => {
    setManualLoading(loading);
    if (loading) {
      startTransition(() => setOptimisticLoading(true));
    }
  };

  const getBaseMutationPayload = () => ({
    surveyId,
    propertyId: ownerId,
    leaseCompId: ownerId,
    mediaId: eachFile?.mediaId || "",
    mediaType,
    ownerType: "LEASE_COMP" as const,
  });

  const getThumbnailUrl = () => {
    const cloudfrontUrl = eachFile?.cloudfrontUrl || "";
    const hasExtension = cloudfrontUrl.split("/").pop()?.includes(".");
    return hasExtension
      ? `${cloudfrontUrl}?d=thumbnail`
      : `${cloudfrontUrl}_thumb`;
  };

  const handleRenameAttachment = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    isEnableEdit(false);
    if (e.target.value.trim() === fileExtension) return;
    setFileName(e.target.value);
    // TODO: Implement update endpoint for lease comp media rename
    console.warn("Rename feature not yet available for lease comp media");
  };

  const handleVisibilityStatus = () => {
    setVisibility(!visibility);

    updateLeaseCompMediaStatus({
      ...eachFile,
      leaseCompId: ownerId,
      isHidden: !visibility,
    })
      .unwrap()
      .catch((_error) =>
        console.error("Failed to update lease comp photo visibility"),
      );
  };

  const deleteHandler = async () => {
    setOptimisticLoadingState(true);
    try {
      await deleteLeaseCompImage(getBaseMutationPayload()).unwrap();
    } catch (_error) {
      console.error("Failed to delete attachment");
    } finally {
      setManualLoading(false);
    }
  };

  const dragPreventionProps = {
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
  };

  const spinnerProps = {
    title: "Loading attachment",
    size: "medium" as const,
    isWhite: false,
    determinate: false,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames("flex transition-colors group", {
        "opacity-50 bg-slate-100": isDragging,
      })}
      {...attributes}
      {...listeners}
    >
      <div
        key={eachFile?.mediaId}
        className="mb-2 w-50 relative cursor-move thumbnail-container"
      >
        {!isTempFile ? (
          <>
            {optimisticLoading && (
              <EmeraldProgressSpinner
                {...spinnerProps}
                className="absolute top-2/5 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-50 z-10"
              />
            )}
            <TransformedImage
              imageUrl={getThumbnailUrl()}
              alt={`Photo ${fileName}`}
              className={classNames(
                "w-50 h-37.5 bg-cover bg-center overflow-hidden",
                {
                  "opacity-30": visibility || optimisticLoading,
                },
              )}
              asBackground={true}
              showFallback={true}
            />
          </>
        ) : (
          <div className="w-50 h-37.5 flex justify-center items-center">
            <EmeraldProgressSpinner {...spinnerProps} />
          </div>
        )}

        {!optimisticLoading && !isTempFile && (
          <div {...dragPreventionProps} className="absolute top-2 left-2">
            <EmeraldMenu
              triggerElement={<EmeraldIcon icon={"more_vert"} />}
              opened={false}
              closeOnOutsideClick={true}
              position={"auto"}
              positionX={50}
              positionY={50}
              dropdownWidth={500}
              className="bg-white rounded-full cursor-pointer z-10"
            >
              <EmeraldList>
                <EmeraldListItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVisibilityStatus();
                  }}
                >
                  <span>{visibility ? "Active" : "Inactive"}</span>
                </EmeraldListItem>
                <EmeraldListItem
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteHandler();
                  }}
                >
                  <span>Delete</span>
                </EmeraldListItem>
              </EmeraldList>
            </EmeraldMenu>
          </div>
        )}

        {!enableEdit ? (
          <div className="text-xs my-2 text-edit-icon wrap-break-word">
            <span>{fileName}</span>
            <div
              className="inline-block"
              {...dragPreventionProps}
              onClick={(e) => {
                e.stopPropagation();
                isEnableEdit(true);
              }}
            >
              <EmeraldIcon
                icon="edit"
                type="filled"
                variant="icon"
                className="flex-1 align-bottom ml-2 cursor-pointer"
              />
            </div>
          </div>
        ) : (
          <div {...dragPreventionProps}>
            <EmeraldInputV2
              type="text"
              label=""
              value={fileName}
              size="small"
              className="without-label my-2"
              isFocused
              onBlur={handleRenameAttachment}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaseCompPhotoThumbnail;
